import { Alert, App as AntApp, DatePicker, Form, InputNumber, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { toAmount } from "../app/balance.ts";
import { billingKeys, subjectKinds, subjectRateGroups, type SubjectKind, type SubjectRateGroup, type TeacherRate } from "../app/billing.ts";

const ANY_GROUP = "any";
const GRADES = [5, 6, 7, 8, 9, 10, 11, 12];

/** Field names match the API; teacherShare is entered as a percentage here and sent as a fraction. */
interface RateForm {
  subjectGroup?: SubjectRateGroup | typeof ANY_GROUP;
  kind?: SubjectKind;
  gradeFrom?: number;
  gradeTo?: number;
  monthlyRatePerStudent?: number;
  lessonsPerMonth?: number;
  teacherShare?: number;
  validFrom?: Dayjs;
}

const toFraction = (percent: number | undefined) => (percent === undefined ? null : Math.round(percent * 100) / 10000);

/** Adds a teacher rate version from a date, or corrects the amounts of an unused version. */
export function TeacherRateModal({ target, onClose }: { target: TeacherRate | "new" | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<RateForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const existing = target !== undefined && target !== "new" ? target : undefined;
  const watchedKind = Form.useWatch("kind", form);
  const kind = existing?.kind ?? watchedKind;
  const fromTimetable = kind === "Secondary";

  useEffect(() => {
    if (target === undefined) return;
    form.resetFields();
    if (existing) {
      form.setFieldsValue({
        monthlyRatePerStudent: toAmount(existing.monthlyRatePerStudent),
        ...(existing.lessonsPerMonth === null ? {} : { lessonsPerMonth: Number(existing.lessonsPerMonth) }),
        teacherShare: toAmount(existing.teacherShare) * 100,
      });
    } else {
      form.setFieldsValue({ subjectGroup: ANY_GROUP });
    }
    setSummary([]);
  }, [target, existing, form]);

  const save = useMutation({
    mutationFn: async (values: RateForm) => {
      const amounts = {
        monthlyRatePerStudent: values.monthlyRatePerStudent ?? null,
        lessonsPerMonth: fromTimetable ? null : (values.lessonsPerMonth ?? null),
        teacherShare: toFraction(values.teacherShare),
      };
      const result = existing
        ? await api.PUT("/billing/teacher-rates/{id}", { params: { path: { id: existing.id } }, body: amounts })
        : await api.POST("/billing/teacher-rates", {
            body: {
              ...amounts,
              subjectGroup: values.subjectGroup === ANY_GROUP || values.subjectGroup === undefined ? null : values.subjectGroup,
              kind: values.kind ?? null,
              gradeFrom: values.gradeFrom ?? null,
              gradeTo: values.gradeTo ?? null,
              validFrom: values.validFrom?.format("YYYY-MM-DD") ?? null,
            },
          });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t(existing ? "billing.saved" : "billing.versionAdded"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = [{ required: true, message: t("common.required") }];
  const gradeOptions = GRADES.map((g) => ({ value: g, label: t("billing.gradeLabel", { grade: g }) }));
  return (
    <Modal
      open={target !== undefined}
      title={existing ? t("billing.rates.editTitle") : t("billing.rates.createTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={existing ? t("billing.editHint") : t("billing.newVersionHint")} />
      <Form<RateForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        {!existing && (
          <>
            <Form.Item name="subjectGroup" label={t("billing.rates.group")} extra={t("billing.rates.groupHint")}>
              <Select
                options={[
                  { value: ANY_GROUP, label: t("billing.rates.anyGroup") },
                  ...subjectRateGroups.map((g) => ({ value: g, label: t(`subjects.rateGroups.${g}`) })),
                ]}
              />
            </Form.Item>
            <Form.Item name="kind" label={t("billing.rates.kind")} rules={required}>
              <Select options={subjectKinds.map((k) => ({ value: k, label: t(`courses.kinds.${k}`) }))} />
            </Form.Item>
            <div style={{ display: "flex", gap: 12 }}>
              <Form.Item name="gradeFrom" label={t("billing.rates.gradeFrom")} rules={required} style={{ flex: 1 }}>
                <Select options={gradeOptions} />
              </Form.Item>
              <Form.Item name="gradeTo" label={t("billing.rates.gradeTo")} rules={required} style={{ flex: 1 }}>
                <Select options={gradeOptions} />
              </Form.Item>
            </div>
          </>
        )}
        <Form.Item name="monthlyRatePerStudent" label={t("billing.rates.monthlyRate")} rules={required}>
          <InputNumber<number> style={{ width: "100%" }} min={0} precision={2} step={10000} />
        </Form.Item>
        <Form.Item
          name="lessonsPerMonth"
          label={t("billing.rates.lessonsPerMonth")}
          rules={fromTimetable ? [] : required}
          extra={fromTimetable ? t("billing.rates.fromTimetableHint") : t("billing.rates.lessonsHint")}
        >
          <InputNumber<number> style={{ width: "100%" }} min={1} max={200} precision={0} disabled={fromTimetable} />
        </Form.Item>
        <Form.Item name="teacherShare" label={t("billing.rates.teacherShare")} rules={required} extra={t("billing.rates.teacherShareHint")}>
          <InputNumber<number> style={{ width: "100%" }} min={0.01} max={100} precision={2} suffix="%" />
        </Form.Item>
        {!existing && (
          <Form.Item name="validFrom" label={t("billing.validFrom")} rules={required} extra={t("billing.validFromHint")}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
